import { useEffect, useMemo, useState } from "react";
import { getAppointmentsByPatientId } from "../services/appointmentService";
import { getInvoicesByPatientId } from "../services/invoiceService";
import { getLatestOpdConsultationWaiverAnchorDate } from "../utils/patientPeriodSummary";
import { requiresConsultationPaymentAfterVisitGap } from "../utils/opdConsultationWaiverUtils";
import {
  APPOINTMENT_INVOICE_TYPES,
  getAppointmentInvoiceTypeOptions,
  isAppointmentInvoiceSubmitDisabled,
  isFirstOpdBillingVisit,
  validateAppointmentInvoiceAmount,
} from "../utils/appointmentInvoiceTypeUtils";
import { useOpdConsultationWaiverSettings } from "./useOpdConsultationWaiverSettings";

const appointmentRecordId = (appointment) =>
  appointment?.id ?? appointment?.appointmentId ?? appointment?.appointment_id ?? null;

/**
 * Billing rules for Confirm Appointment — same REGISTRATION / CONSULTATION
 * and amount rules as front-desk Add Patient. The appointment being confirmed
 * is excluded so a first portal booking still bills as a new (REGISTRATION) visit.
 */
export function useConfirmAppointmentBilling({
  open,
  appointment,
  user,
  amount,
  paymentMode,
}) {
  const consultationWaiverSettings = useOpdConsultationWaiverSettings(user, {
    enabled: open,
  });
  const [invoiceType, setInvoiceType] = useState(
    APPOINTMENT_INVOICE_TYPES.CONSULTATION
  );
  const [isFirstOpdVisit, setIsFirstOpdVisit] = useState(false);
  const [allowRegistrationAfterGap, setAllowRegistrationAfterGap] =
    useState(false);

  const patientId =
    appointment?.patientId ?? appointment?.patient_id ?? null;
  const currentAppointmentId = appointmentRecordId(appointment);

  useEffect(() => {
    if (!open) {
      setInvoiceType(APPOINTMENT_INVOICE_TYPES.CONSULTATION);
      setIsFirstOpdVisit(false);
      setAllowRegistrationAfterGap(false);
      return;
    }

    let isMounted = true;

    const loadBillingContext = async () => {
      if (patientId == null || patientId === "") {
        if (isMounted) {
          setIsFirstOpdVisit(true);
          setAllowRegistrationAfterGap(false);
          setInvoiceType(APPOINTMENT_INVOICE_TYPES.REGISTRATION);
        }
        return;
      }

      try {
        const [invoices, appointments] = await Promise.all([
          getInvoicesByPatientId(patientId),
          getAppointmentsByPatientId(patientId),
        ]);
        const otherAppointments = (Array.isArray(appointments)
          ? appointments
          : []
        ).filter((item) => {
          const id = appointmentRecordId(item);
          if (id == null || currentAppointmentId == null) return true;
          return String(id) !== String(currentAppointmentId);
        });
        const firstOpdVisit = isFirstOpdBillingVisit({
          invoices,
          appointments: otherAppointments,
        });
        const latestWaiverAnchorDate = getLatestOpdConsultationWaiverAnchorDate(
          invoices,
          null
        );
        const requiresPayment = requiresConsultationPaymentAfterVisitGap(
          latestWaiverAnchorDate,
          consultationWaiverSettings
        );

        if (isMounted) {
          setIsFirstOpdVisit(firstOpdVisit);
          setAllowRegistrationAfterGap(requiresPayment);
          const options = getAppointmentInvoiceTypeOptions({
            hasExistingPatient: true,
            isFirstOpdBillingVisit: firstOpdVisit,
          });
          setInvoiceType(options[0] || APPOINTMENT_INVOICE_TYPES.CONSULTATION);
        }
      } catch (error) {
        console.warn(
          "[useConfirmAppointmentBilling] Failed to load invoice type context:",
          error
        );
        if (isMounted) {
          setIsFirstOpdVisit(false);
          setAllowRegistrationAfterGap(
            requiresConsultationPaymentAfterVisitGap(
              null,
              consultationWaiverSettings
            )
          );
          setInvoiceType(APPOINTMENT_INVOICE_TYPES.CONSULTATION);
        }
      }
    };

    loadBillingContext();

    return () => {
      isMounted = false;
    };
  }, [
    open,
    patientId,
    currentAppointmentId,
    consultationWaiverSettings.enabled,
    consultationWaiverSettings.gapDays,
  ]);

  const invoiceTypeOptions = useMemo(
    () =>
      getAppointmentInvoiceTypeOptions({
        hasExistingPatient: true,
        isFirstOpdBillingVisit: isFirstOpdVisit,
      }),
    [isFirstOpdVisit]
  );

  const billingContext = useMemo(
    () => ({
      hasExistingPatient: true,
      isFirstOpdBillingVisit: isFirstOpdVisit,
      allowRegistrationAfterGap,
    }),
    [isFirstOpdVisit, allowRegistrationAfterGap]
  );

  const amountError =
    validateAppointmentInvoiceAmount({
      amount,
      indicator: invoiceType,
      ...billingContext,
    }) ||
    (!(Number(amount) > 0) ? "Please enter the appointment amount" : null);

  const submitDisabled =
    !(Number(amount) > 0) ||
    isAppointmentInvoiceSubmitDisabled({
      amount,
      indicator: invoiceType,
      paymentMode,
      ...billingContext,
    });

  return {
    invoiceType,
    setInvoiceType,
    invoiceTypeOptions,
    amountError,
    submitDisabled,
    billingContext,
  };
}

export default useConfirmAppointmentBilling;
